import {
	Box,
	Icons,
	Table,
	AsyncSelect,
	Dropdown,
	Stack,
	Text,
	Inline,
	joinAttributes,
	Time,
	Badge,
	CheckboxInput,
	Button,
	Spinner,
} from "@sembark-travel/ui-base"
import {
	NavLink,
	queryToSearch,
	useLocationQuery,
	ButtonLink,
	Link,
} from "@sembark-travel/ui-router"
import {
	ListView,
	TSearchParams,
	useSearch,
	Search,
} from "@sembark-travel/ui-list"
import { ICursorListResponse, useXHR } from "@sembark-travel/xhr"
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react"
import { Omit } from "utility-types"
import { showSnackbar } from "@sembark-travel/ui-snackbar"
import { PERMISSIONS, useAuthUser, useCheckPermissions } from "../Auth"
import {
	DateRangePickerField,
	Form,
	isTruthy,
	SelectField,
	SubmissionError,
	SwitchInputField,
	TextInputField,
	useFieldValue,
	validateFormValues,
	withServerErrors,
} from "@sembark-travel/ui-form"
import config from "../config"
import { generatePath } from "../router-utils"
import { EditItemInDialog } from "./EditItem"
import { AddTripSourceDialog } from "./NewItem"
import { ITripSource, ITripSourceTag, tripSourcesXHR } from "./store"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	parseDateFromQuery,
	startOf,
} from "@sembark-travel/datetime-utils"
import { ILocation, SelectLocations } from "../Locations"
import { TemporaryStorage } from "../DownloadManager"
import { SelectTripSourceTags } from "./Tag"
import {
	ConfirmationDialog,
	ConfirmationDialogProvider,
	Dialog,
	useDialog,
} from "@sembark-travel/ui-dialog"
import { SelectTeams, TTeam } from "../Teams"
import { IUser, SelectUsers } from "../Users"
import { ITag, SelectTags, TagBadges } from "../Tags"
import { checkFilterAppliedFn } from "../utils"
import * as Validator from "yup"
import { Batch } from "../Trips/List"

type TFilters = TSearchParams & {
	stats_start?: Date
	stats_end?: Date
	created_after?: Date
	created_before?: Date
	locations?: Array<ILocation>
	tags?: Array<ITripSourceTag>
	teams?: Array<TTeam>
	created_by?: Array<IUser>
	disabled_only?: 1 | 0
	no_teams?: 1 | 0
	is_agent?: 1 | 0
}

type TFiltersInQuery = TSearchParams & {
	ss?: string
	se?: string
	ca?: string
	cb?: string
	locations?: Array<string>
	tags?: Array<string>
	teams?: Array<string>
	created_by?: Array<string>
	disabled_only?: 1 | 0
	no_teams?: 1 | 0
	is_agent?: 1 | 0
}

function filtersToQuery(filters: TFilters): TFiltersInQuery {
	const {
		q,
		page,
		stats_start,
		stats_end,
		created_after,
		created_before,
		locations,
		tags,
		teams,
		created_by,
		disabled_only,
		no_teams,
	} = filters
	const query: TFiltersInQuery = {}
	if (page) {
		query.page = page
	}
	if (q) {
		query.q = q
	}
	if (stats_start) {
		query.ss = dateToQuery(stats_start)
	}
	if (stats_end) {
		query.se = dateToQuery(stats_end)
	}
	if (created_after) {
		query.ca = dateToQuery(created_after)
	}
	if (created_before) {
		query.cb = dateToQuery(created_before)
	}
	if (locations?.length) {
		query.locations = locations.map((l) => `${l.id}__${l.full_name}`)
	}
	if (tags?.length) {
		query.tags = tags.map((t) => `${t.id}__${t.name}`)
	}
	if (teams?.length) {
		query.teams = teams.map((t) => `${t.id}__${t.name}`)
	}
	if (created_by?.length) {
		query.created_by = created_by.map((c) => `${c.id}__${c.name}`)
	}
	if (disabled_only) {
		query.disabled_only = 1
	}
	if (no_teams) {
		query.no_teams = 1
	}
	return query
}

function queryToFilters(query: TFiltersInQuery): TFilters {
	const {
		q,
		page,
		ss,
		se,
		ca,
		cb,
		locations,
		tags,
		teams,
		created_by,
		disabled_only,
		no_teams,
	} = query
	const filters: TFilters = {}
	if (page) {
		filters.page = page
	}
	if (q) {
		filters.q = q
	}
	if (ss) {
		filters.stats_start = parseDateFromQuery(ss)
	}
	if (se) {
		filters.stats_end = parseDateFromQuery(se)
	}
	if (ca) {
		filters.created_after = parseDateFromQuery(ca)
	}
	if (cb) {
		filters.created_before = parseDateFromQuery(cb)
	}
	if (locations?.length) {
		filters.locations = locations.map((l) => {
			const [id, ...name] = l.split("__")
			return {
				id,
				full_name: name.join("__"),
			} as never as ILocation
		})
	}
	if (tags?.length) {
		filters.tags = tags.map((t) => {
			const [id, ...name] = t.split("__")
			return { id: id, name: name.join("__") } as never as ITripSourceTag
		})
	}
	if (teams?.length) {
		filters.teams = teams.map((t) => {
			const [id, ...name] = t.split("__")
			return { id, name: name.join("__") } as never as TTeam
		})
	}
	if (created_by) {
		filters.created_by = created_by.map((c) => {
			const [id, ...name] = c.split("__")
			return {
				id,
				name: name.join("__"),
			} as never as IUser
		})
	}
	if (disabled_only) {
		filters.disabled_only = 1
	}
	if (no_teams) {
		filters.no_teams = 1
	}
	return filters
}

function filtersToRequest({
	stats_start,
	stats_end,
	created_after,
	created_before,
	locations,
	tags,
	disabled_only,
	no_teams,
	teams,
	created_by,
	is_agent,
	...params
}: TFilters) {
	// no_teams and teams are mutually exclusive filters if both somehow end up set, no_teams wins and teams is dropped.
	return {
		...params,
		stats_start: stats_start
			? dateToUTCString(startOf(stats_start, "day"))
			: null,
		stats_end: stats_end ? dateToUTCString(endOf(stats_end, "day")) : null,
		created_after: created_after
			? dateToUTCString(startOf(created_after, "day"))
			: null,
		created_before: created_before
			? dateToUTCString(endOf(created_before, "day"))
			: null,
		locations: locations?.length ? locations.map((l) => l.id) : [],
		tags: tags?.length ? tags.map((t) => t.id) : [],
		teams: !isTruthy(no_teams) && teams?.length ? teams.map((t) => t.id) : [],
		created_by: created_by?.length ? created_by.map((c) => c.id) : [],
		disabled_only: isTruthy(disabled_only) ? 1 : undefined,
		no_teams: isTruthy(no_teams) ? 1 : undefined,
		is_agent: is_agent !== undefined ? (isTruthy(is_agent) ? 1 : 0) : undefined,
	}
}

export function List({
	title = "Trip Sources",
	hideBulkActions = false,
}: {
	title?: string
	hideBulkActions?: boolean
}) {
	const [query, setQuery] = useLocationQuery<TFilters, TFiltersInQuery>({
		toQuery: filtersToQuery,
		fromQuery: queryToFilters,
	})
	const [params, setParams] = useSearch<TFilters>(query)
	const { hasPermission } = useCheckPermissions()
	const { user } = useAuthUser()
	const canViewAllSources =
		hasPermission(PERMISSIONS.ADD_TRIP_DESTINATIONS) ||
		isTruthy(user?.show_only_assigned_trip_sources)
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	const disabledOnly = isTruthy(params.disabled_only)
	const [componentKey, setComponentKey] = useState(0)
	return (
		<Search
			initialParams={params}
			onSearch={(params) => {
				setParams({ ...params, page: 1 })
			}}
			title={title}
			Filters={canViewAllSources ? Filters : undefined}
			actions={({ changeAdvancedFiltersVisibility }) =>
				hasPermission(PERMISSIONS.ADD_TRIP_SOURCES) ? (
					<Inline gap="4">
						<ButtonLink
							to={generatePath("/trip-sources/new")}
							level="primary"
							status="primary"
							anchored
						>
							<Icons.Plus title="Add" /> New Source
						</ButtonLink>
						<Dropdown alignRight="sm">
							<Dropdown.ToggleButton>
								<Icons.DotsVertical />
							</Dropdown.ToggleButton>
							<Dropdown.Menu>
								{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
									<Dropdown.MenuItem
										as={Link}
										to={generatePath("/trip-sources/tags")}
									>
										Trip Source Tags
									</Dropdown.MenuItem>
								) : null}
								<Dropdown.MenuItem
									as={Link}
									to={generatePath("/trip-sources/merge")}
								>
									Merge Duplicate Sources
								</Dropdown.MenuItem>
								{!hideBulkActions && hasPermission(PERMISSIONS.MANAGE_USERS) ? (
									<>
										<BulkAssignTripSource
											onSuccess={() => {
												setComponentKey((key) => ++key)
											}}
											params={params}
											key={`${componentKey}-team`}
											FilterOpen={() => {
												changeAdvancedFiltersVisibility(true)
											}}
											type="team"
										>
											{({ onAssign }) => (
												<Dropdown.MenuItem onClick={onAssign}>
													Trip Source Bulk Team Assign <Icons.NewBadge />
												</Dropdown.MenuItem>
											)}
										</BulkAssignTripSource>
										<BulkAssignTripSource
											onSuccess={() => {
												setComponentKey((key) => ++key)
											}}
											params={params}
											key={`${componentKey}-tag`}
											FilterOpen={() => {
												changeAdvancedFiltersVisibility(true)
											}}
											type="tag"
										>
											{({ onAssign }) => (
												<Dropdown.MenuItem onClick={onAssign}>
													Trip Source Bulk Tag Assign <Icons.NewBadge />
												</Dropdown.MenuItem>
											)}
										</BulkAssignTripSource>
									</>
								) : null}
								{canViewAllSources ? (
									<TemporaryStorage
										initUrl="/trip-sources/download"
										initParams={{
											...filtersToRequest(params),
											timezone_offset: config.timezoneOffset,
										}}
									>
										{({ message, generate, url, reset }) =>
											url ? (
												<Dropdown.MenuItem
													as="a"
													href={url}
													target="_blank"
													download
													onClick={() => reset()}
												>
													{message}
												</Dropdown.MenuItem>
											) : (
												<Dropdown.MenuItem
													onClick={(e: React.MouseEvent) => {
														e.preventDefault()
														generate()
													}}
												>
													{message || (
														<>
															<Icons.DocumentDownload /> Download as CSV/Excel
														</>
													)}
												</Dropdown.MenuItem>
											)
										}
									</TemporaryStorage>
								) : null}
							</Dropdown.Menu>
						</Dropdown>
					</Inline>
				) : null
			}
		>
			<ListView<ITripSource, TFilters>
				pageKey="trip-sources"
				fetch={(xhr, params) =>
					tripSourcesXHR(xhr).getTripSources({
						...filtersToRequest(params),
						include: "connected_tenant,address,created_by,tags,teams",
						sort: "-created_at",
						page: canViewAllSources ? params.page : 1,
					})
				}
				onPageChange={(page) => setParams({ ...params, page })}
				params={params}
				paginationDisabled={!canViewAllSources}
				key={componentKey}
			>
				{({ items: tripSources, refresh }) => (
					<Table
						striped
						bordered
						headers={["Name", "Created By", "Tags", "Teams", ""]}
						responsive
						alignCols={{ 4: "right" }}
						rows={tripSources.map((tripSource) => [
							<Link
								to={generatePath("/trip-sources/:tripSourceId", {
									tripSourceId: tripSource.id.toString(),
								})}
								color="accent"
								fontWeight="semibold"
								anchored
							>
								<Inline gap="6" alignItems={"start"}>
									<Stack gap="px">
										<Text>
											{tripSource.name} ({tripSource.short_name})
										</Text>
										<Inline fontSize="sm">
											{joinAttributes(
												tripSource.address
													? tripSource.address.location.name
													: null,
												tripSource.is_agent ? "B2B Agent" : null
											)}
										</Inline>
									</Stack>
									{disabledOnly ? (
										<Badge status="warning">Disabled</Badge>
									) : null}
								</Inline>
							</Link>,
							<Stack gap="px">
								<Text>{tripSource.created_by?.name || "You"}</Text>
								<Text color="muted" fontSize="sm">
									<Time timestamp={tripSource.created_at} />
								</Text>
							</Stack>,
							<TagBadges tags={tripSource.tags} />,
							<Inline color={"muted"}>
								{(tripSource.teams || []).length > 0
									? (tripSource.teams || [])
											.map((team) => team.name)
											.join(" • ")
									: "-"}
							</Inline>,
							<Box>
								{hasPermission(PERMISSIONS.ADD_TRIP_SOURCES) ? (
									<Dropdown alignRight>
										<Dropdown.ToggleButton level="tertiary" size="sm">
											<Icons.DotsVertical />
										</Dropdown.ToggleButton>
										<Dropdown.Menu>
											{disabledOnly ? null : (
												<EditItemInDialog
													tripSource={tripSource}
													onSuccess={() => {
														refresh()
													}}
												>
													{({ edit }) => (
														<Dropdown.MenuItem onClick={() => edit()}>
															<Icons.Pencil /> Edit Details
														</Dropdown.MenuItem>
													)}
												</EditItemInDialog>
											)}
											<NavLink
												to={
													generatePath("/trips") +
													queryToSearch({
														sources: [`${tripSource.id}_${tripSource.name}`],
														status: "all",
													})
												}
											>
												<Icons.Preview /> View Queries
											</NavLink>
											{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
												<DisableEnableTripSource
													tripSource={tripSource}
													isDisabled={disabledOnly}
													onSuccess={() => {
														refresh()
													}}
												>
													{({ onToggleStatus, isProcessing }) => (
														<Dropdown.MenuItem
															onClick={onToggleStatus}
															color={disabledOnly ? "success" : "danger"}
														>
															{isProcessing ? (
																<>
																	<Icons.Refresh spin /> Processing...
																</>
															) : disabledOnly ? (
																<>
																	<Icons.Refresh /> Enable
																</>
															) : (
																<>
																	<Icons.Ban /> Disable
																</>
															)}
														</Dropdown.MenuItem>
													)}
												</DisableEnableTripSource>
											) : null}
										</Dropdown.Menu>
									</Dropdown>
								) : null}
							</Box>,
						])}
					/>
				)}
			</ListView>
		</Search>
	)
}

export function DisableEnableTripSource({
	tripSource,
	onSuccess,
	children,
	isDisabled,
}: {
	tripSource: ITripSource
	onSuccess?: () => void
	children: (props: {
		onToggleStatus: () => void
		isProcessing: boolean
	}) => React.ReactNode
	isDisabled: boolean
}) {
	const xhr = useXHR()
	const [isProcessing, setIsProcessing] = useState(false)

	return (
		<ConfirmationDialogProvider>
			{({ confirm }) => (
				<>
					{children({
						isProcessing,
						onToggleStatus: async () => {
							if (isProcessing) return
							if (await confirm()) {
								setIsProcessing(true)
								try {
									const endpoint = `/trip-sources/${tripSource.id}${isDisabled ? "/restore" : ""}`
									const method = isDisabled ? "patch" : "delete"
									const response = await xhr[method](endpoint)

									const message =
										response.data?.message ||
										`${tripSource.name} has been ${isDisabled ? "enabled" : "disabled"} successfully.`

									showSnackbar(message)
									onSuccess?.()
								} catch (e) {
									const error = e as Error
									showSnackbar(
										error.message || "Something went wrong. Please try again."
									)
								} finally {
									setIsProcessing(false)
								}
							}
						},
					})}
					<ConfirmationDialog
						title={isDisabled ? "Enable Trip Source" : "Disable Trip Source"}
						destructiveAction={!isDisabled}
						acceptActionLabel={
							isDisabled ? "Yes, Enable It" : "I understand, Disable It"
						}
					>
						<Stack gap="2">
							{isDisabled ? (
								<Text>Are you sure you want to enable this trip source? </Text>
							) : (
								<Text>
									Are you sure you want to disable this trip source? You can
									always enable this source again if required.
								</Text>
							)}
						</Stack>
					</ConfirmationDialog>
				</>
			)}
		</ConfirmationDialogProvider>
	)
}

function Filters() {
	const { hasPermission } = useCheckPermissions()
	const { value: teams } = useFieldValue<Array<TTeam>>("teams")
	const { value: no_teams } = useFieldValue<boolean>("no_teams")
	return (
		<Stack gap="4">
			<DateRangePickerField
				fromName="created_after"
				toName="created_before"
				label="Created Between"
				dateFormat="DD MMM, YYYY"
			/>
			{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
				<>
					<SelectField
						select={SelectTeams}
						name="teams"
						label="Teams"
						multiple
						disabled={isTruthy(no_teams)}
					/>
					<SelectField
						select={SelectTripSourceTags}
						name="tags"
						multiple
						label="Tag"
						creatable={false}
					/>
				</>
			) : null}
			<SelectField
				select={SelectLocations}
				name="locations"
				multiple
				label="Cities/Locations"
				creatable={false}
				addressableType="trip_sources"
			/>
			<DateRangePickerField
				fromName="stats_start"
				toName="stats_end"
				label="Stats Between"
				dateFormat="DD MMM, YYYY"
			/>
			<SelectField
				select={SelectUsers}
				name="created_by"
				multiple
				label="Created By"
				fetchOnMount
			/>
			<SwitchInputField name="disabled_only" label="Disabled Only" />
			{hasPermission(PERMISSIONS.MANAGE_USERS) ? (
				<SwitchInputField
					name="no_teams"
					label="No Teams"
					disabled={isTruthy(teams)}
				/>
			) : null}
		</Stack>
	)
}

interface RefHandle {
	getMarkedIds: () => number[]
	isItemZero: boolean
}

function BulkAssignTripSource({
	children,
	params,
	onSuccess,
	FilterOpen,
	type,
}: {
	children: (props: { onAssign: () => void }) => React.ReactNode
	params: TFilters
	onSuccess: () => void
	FilterOpen: () => void
	type: "team" | "tag"
}) {
	const [isOpen, openDialog, closeDialog] = useDialog()
	const [count, setCount] = useState({
		count: 0,
		totalCount: 0,
	})

	const xhr = useXHR()
	const childRef = useRef<RefHandle>(null)
	const inProgressRef = useRef(false)
	const [inProgressState, setInProgressState] = useState(false)
	const [isSelectAll, setIsSelectAll] = useState(false)

	const areFiltersApplied = useMemo(
		() => checkFilterAppliedFn(params, "cursor", "page"),
		[params]
	)
	const isTeam = type === "team"

	const effectiveParams: TFilters = useMemo(
		() => (isTeam ? params : { ...params, is_agent: 1 }),
		[isTeam, params]
	)

	const handleOpenDialog = useCallback(() => {
		setCount({
			count: 0,
			totalCount: 0,
		})
		setIsSelectAll(false)
		openDialog()
	}, [openDialog])

	const handleCloseDialog = useCallback(() => {
		closeDialog()
		setCount({
			count: 0,
			totalCount: 0,
		})
		setIsSelectAll(false)
	}, [closeDialog])

	useEffect(() => {
		if (!inProgressRef.current && inProgressState) {
			showSnackbar(
				isTeam
					? "Assigning Team to Trip Sources..."
					: "Assigning Tag to Trip Sources..."
			)
		}
		if (inProgressRef.current && !inProgressState) {
			showSnackbar(
				isTeam
					? "Team assigned to Trip Sources successfully!"
					: "Tag assigned to Trip Sources successfully!"
			)
			closeDialog()
		}
		inProgressRef.current = inProgressState
	}, [closeDialog, inProgressState, isTeam])
	useEffect(() => {
		async function getCount() {
			try {
				const item = await tripSourcesXHR(xhr).getTripSources({
					...filtersToRequest(effectiveParams),
					limit: 1,
					page: 1,
					pagination: "paginate",
				})
				setCount((val) => ({
					...val,
					totalCount: item.meta.total || 0,
				}))
			} catch (e) {
				console.log(e)
			}
		}
		if (isOpen && areFiltersApplied) {
			getCount()
		}
	}, [areFiltersApplied, effectiveParams, isOpen, xhr])
	function decreaseIncrease(type: "decrease" | "increase") {
		setCount((val) => ({
			...val,
			count: type === "decrease" ? val.count - 1 : val.count + 1,
		}))
	}
	const openFilter = () => {
		handleCloseDialog()
		FilterOpen()
	}

	if (isTruthy(params.disabled_only)) return null
	return (
		<Batch onFinish={onSuccess}>
			{({ init, xhr, inProgress }) => {
				if (inProgress !== inProgressState) {
					setInProgressState(inProgress)
				}
				return (
					<>
						{children({ onAssign: handleOpenDialog })}
						<Dialog
							open={isOpen}
							onClose={handleCloseDialog}
							title={
								isTeam
									? "Trip Source Bulk Team Assign"
									: "Trip Source Bulk Tag Assign"
							}
							lg
						>
							<Form<{
								confirmed_trip_sources_count: number
								assign_value: TTeam | ITag
							}>
								validate={validateFormValues(
									Validator.object().shape({
										confirmed_trip_sources_count: Validator.number()
											.equals(
												[count.count],
												`Please type ${count.count} to confirm the selection`
											)
											.not([0], "Please type a valid number")
											.required(
												`Please type ${count.count} to confirm the selection`
											),
										assign_value: Validator.object().required(
											`Please select a ${isTeam ? "Team" : "Tag"} to assign`
										),
									})
								)}
								onSubmit={withServerErrors(async ({ assign_value }) => {
									const endpoint = isTeam
										? "/trip-sources/bulk-assign-team"
										: "/trip-sources/bulk-assign-tag"
									const assignmentIdField = isTeam
										? "assign_team_id"
										: "assign_tag_id"

									const resp = await xhr.post(endpoint, {
										...filtersToRequest(effectiveParams),
										[assignmentIdField]: assign_value.id,
										...(isSelectAll
											? {
													exclusions: childRef.current?.getMarkedIds(),
												}
											: {
													inclusions: childRef.current?.getMarkedIds(),
												}),
									})
									init(resp.data.data)
								})}
								subscription={{ submitting: true }}
							>
								{({ submitting, handleSubmit }) => (
									<form noValidate onSubmit={handleSubmit}>
										<Dialog.Body padding="8">
											<Stack gap="1">
												<Inline justifyContent="between" gap="2">
													<Stack gap="1">
														<Text fontSize="lg" fontWeight="semibold">
															Please review details
														</Text>
														{!isTeam ? (
															<Text fontSize="sm" color="muted">
																Showing B2B Agent trip sources only
															</Text>
														) : null}
													</Stack>
													{!isSelectAll &&
													areFiltersApplied &&
													!childRef.current?.isItemZero ? (
														<Inline
															cursor="pointer"
															as="label"
															alignItems="center"
															gap="2"
														>
															<CheckboxInput
																checked={isSelectAll}
																onChange={() => {
																	setIsSelectAll(true)
																	setCount((val) => ({
																		...val,
																		count: val.totalCount,
																	}))
																}}
																name="selected_all"
																id="selected_all"
															/>
															<Box htmlFor="selected_all" as="label">
																Select All
															</Box>
														</Inline>
													) : null}
												</Inline>
												<InfiniteScroll
													decreaseIncrease={decreaseIncrease}
													ref={childRef}
													params={effectiveParams}
													openFilter={openFilter}
													isSelectAll={isSelectAll}
													areFiltersApplied={areFiltersApplied}
													isTagAssign={!isTeam}
												/>
											</Stack>
											{areFiltersApplied && !childRef.current?.isItemZero ? (
												<Inline gap="4" flexWrap="wrap">
													{isTeam ? (
														<SelectField
															name="assign_value"
															select={SelectTeams}
															label="Choose Team"
															fetchOnMount
														/>
													) : (
														<SelectField
															name="assign_value"
															select={SelectTags}
															label="Choose Tag"
															type="trip-source"
															fetchOnMount
															creatable={false}
														/>
													)}
													{count.count ? (
														<TextInputField
															label={`Please type ${count.count} to confirm`}
															name="confirmed_trip_sources_count"
															type="number"
															aria-label={`Enter the Trip Sources count to confirm ${type} assignment`}
															required
														/>
													) : null}
												</Inline>
											) : null}
										</Dialog.Body>
										{areFiltersApplied && !childRef.current?.isItemZero ? (
											<Dialog.Footer>
												<Stack gap="4">
													<SubmissionError />
													<Inline gap="2">
														<Button
															disabled={
																submitting || inProgressState || !count.count
															}
															type="submit"
															level="primary"
														>
															{submitting || inProgressState
																? isTeam
																	? "Assigning Team..."
																	: "Assigning Tag..."
																: isTeam
																	? "Assign Team"
																	: "Assign Tag"}
														</Button>
														<Button
															disabled={submitting}
															onClick={handleCloseDialog}
															level="tertiary"
														>
															Cancel
														</Button>
													</Inline>
												</Stack>
											</Dialog.Footer>
										) : null}
									</form>
								)}
							</Form>
						</Dialog>
					</>
				)
			}}
		</Batch>
	)
}

export type TBatch = {
	id: string
	name: string
	totalJobs: number
	pendingJobs: number
	processedJobs: number
	progress: number
	failedJobs: number
	createdAt: string
	cancelledAt: string | null
	finishedAt: string | null
}

type CursorMeta = ICursorListResponse<unknown>["meta"]
type TInfiniteScrollBase = {
	params: TFilters
	decreaseIncrease: (type: "decrease" | "increase") => void
	openFilter: () => void
	isSelectAll: boolean
	areFiltersApplied: boolean
	isTagAssign?: boolean
}

function InfiniteScrollBase(
	{
		params,
		decreaseIncrease,
		openFilter,
		isSelectAll,
		areFiltersApplied,
		isTagAssign,
	}: TInfiniteScrollBase,

	ref: React.ForwardedRef<RefHandle>
) {
	const [items, setItems] = useState<ITripSource[]>([])
	const [selectedItems, setSelectedItems] = useState<number[]>([])
	type Status = {
		isLoading: boolean
		error: string
	}
	const [status, setStatus] = useState<Status>({
		isLoading: false,
		error: "",
	})

	const groupRef = useRef<HTMLDivElement>(null)
	const isFetchingRef = useRef(false)
	const pageInfo = useRef<CursorMeta | undefined>(undefined)
	const SCROLL_THRESHOLD = 100
	const xhr = useXHR()

	const getMoreTripSources = useCallback(async () => {
		if (isFetchingRef.current) return
		setStatus((val) => ({ ...val, isLoading: true }))
		isFetchingRef.current = true
		try {
			const { page, ...rest } = filtersToRequest(params)
			const item = await tripSourcesXHR(xhr).getTripSourcesWithCursorPagination(
				{
					...rest,
					include: "created_by,address",
					limit: 10,
					cursor: pageInfo.current?.next_cursor,
				}
			)
			pageInfo.current = item.meta

			setItems((prev) =>
				pageInfo.current?.prev_cursor ? [...prev, ...item.data] : [...item.data]
			)
			setStatus((val) => ({ error: "", isLoading: false }))
		} catch (e) {
			const error = e as Error
			setStatus((val) => ({
				...val,
				error: error.message || "Something went wrong",
			}))
		} finally {
			isFetchingRef.current = false
		}
	}, [xhr, params])
	useEffect(() => {
		if (areFiltersApplied) {
			getMoreTripSources()
		}
		let throttleTimeout: ReturnType<typeof setTimeout> | null = null
		const throttledHandleScroll = () => {
			if (throttleTimeout) return
			throttleTimeout = setTimeout(() => {
				const container = groupRef.current?.querySelector("ol")
				if (!container) return

				const isBottom =
					container.scrollHeight - container.scrollTop <=
					container.clientHeight + SCROLL_THRESHOLD

				if (
					isBottom &&
					!isFetchingRef.current &&
					pageInfo.current?.next_cursor
				) {
					getMoreTripSources()
				}

				throttleTimeout = null
			}, 300)
		}
		const container = groupRef.current?.querySelector("ol")
		if (container) {
			container.addEventListener("scroll", throttledHandleScroll)
			return () => {
				container.removeEventListener("scroll", throttledHandleScroll)
				if (throttleTimeout) clearTimeout(throttleTimeout)
			}
		}
	}, [areFiltersApplied, getMoreTripSources, params])

	React.useImperativeHandle(ref, () => ({
		getMarkedIds: function () {
			return selectedItems
		},
		isItemZero: !items.length && !status.isLoading,
	}))
	useEffect(() => {
		setSelectedItems([])
	}, [isSelectAll])

	return (
		<Stack
			padding="4"
			borderWidth="1"
			borderColor="warning"
			rounded="lg"
			marginBottom="4"
			ref={groupRef}
		>
			{status.error ? (
				<Box
					display="flex"
					alignItems="center"
					gap={"3"}
					padding={"3"}
					borderColor={"danger"}
					borderRadius="md"
				>
					<Box as="span" flex="1" fontWeight="semibold">
						{status.error}
					</Box>
					<Box
						as="button"
						paddingX={"3"}
						paddingY={"1"}
						color="white"
						borderRadius="md"
						fontWeight="semibold"
						onClick={getMoreTripSources}
					>
						Refresh
					</Box>
				</Box>
			) : null}
			<Box
				style={{
					height: "400px",
				}}
				overflowY="auto"
				as="ol"
				display={!items.length && !status.isLoading ? "flex" : "block"}
				flexDirection={"col"}
				justifyContent={"center"}
				alignItems={"center"}
			>
				{!items.length && !status.isLoading ? (
					<Stack alignItems="center" gap="2">
						{!areFiltersApplied ? (
							<>
								<Box as="p">
									Please choose right filter or search option to assign trip
									sources
								</Box>
								<Button onClick={openFilter} type="button">
									Choose Right Filter
								</Button>
							</>
						) : isTagAssign ? (
							<>
								<Box as="p">
									No B2B Agent trip sources found matching the selected filters.
								</Box>
								<Button onClick={openFilter} type="button">
									Change Filters
								</Button>
							</>
						) : (
							<>
								<Box as="p">
									No trip sources found matching the selected filters.
								</Box>
								<Button onClick={openFilter} type="button">
									Change Filters
								</Button>
							</>
						)}
					</Stack>
				) : (
					<Table
						bordered
						responsive
						hover
						headers={[" ", "Name", "Created By"]}
						rows={items.map((tripSource) => {
							const {
								id,
								name,
								short_name,
								address,
								is_agent,
								created_by,
								created_at,
							} = tripSource
							return [
								<CheckboxInput
									id={name + id}
									checked={
										isSelectAll
											? !selectedItems.includes(id)
											: selectedItems.includes(id)
									}
									onChange={(e) => {
										const checked =
											(e.currentTarget.checked && isSelectAll) ||
											(!e.currentTarget.checked && !isSelectAll)
										if (checked) {
											setSelectedItems((ids) =>
												ids.filter((singleId) => singleId !== id)
											)
											decreaseIncrease(isSelectAll ? "increase" : "decrease")
										} else {
											setSelectedItems((ids) => [...ids, id])
											decreaseIncrease(!isSelectAll ? "increase" : "decrease")
										}
									}}
								/>,
								<Link
									to={generatePath("/trip-sources/:tripSourceId", {
										tripSourceId: id.toString(),
									})}
									color="accent"
									fontWeight="semibold"
									anchored
								>
									<Inline gap="6" alignItems={"start"}>
										<Stack gap="px">
											<Text>
												{name} ({short_name})
											</Text>
											<Inline fontSize="sm">
												{joinAttributes(
													address ? address.location.name : null,
													is_agent ? "B2B Agent" : null
												)}
											</Inline>
										</Stack>
									</Inline>
								</Link>,
								<Stack gap="px">
									<Text>{created_by?.name || "You"}</Text>
									<Text color="muted" fontSize="sm">
										<Time timestamp={created_at} />
									</Text>
								</Stack>,
							]
						})}
					/>
				)}

				{status.isLoading ? (
					<Inline justifyContent={"center"} gap={"2"} alignItems={"center"}>
						<Spinner /> Loading more trip sources...
					</Inline>
				) : null}
			</Box>
		</Stack>
	)
}

const InfiniteScroll = React.forwardRef<RefHandle, TInfiniteScrollBase>(
	InfiniteScrollBase
)

export function SelectTripSources({
	includeContacts,
	onCreateSuccess,
	withRecentlyDisabledWithinDays,
	...props
}: {
	includeContacts?: boolean
	onCreateSuccess?: (tripSource: ITripSource) => void
	withRecentlyDisabledWithinDays?: number
} & Omit<React.ComponentProps<typeof AsyncSelect>, "fetch">) {
	const xhr = useXHR()
	const [newName, setNewName] = useState("")
	return (
		<>
			<AsyncSelect
				cacheKey={`trip_sources${withRecentlyDisabledWithinDays ? "_include_recently_deleted" : ""}${includeContacts ? "_with_contacts" : ""}`}
				multiple
				{...props}
				fetch={(q) =>
					tripSourcesXHR(xhr)
						.getTripSources({
							q,
							include: ["address", includeContacts ? "contacts" : "", "tags"]
								.filter(Boolean)
								.join(","),
							sort: "name",
							with_recently_disabled_within_days:
								withRecentlyDisabledWithinDays || undefined,
						})
						.then((resp) => resp.data)
				}
				onCreateNew={(query: string) => {
					setNewName(query)
				}}
				optionRenderer={({ option, created }) =>
					created ? (
						<Box>Add new Source "{option.name}"</Box>
					) : (
						<Box>
							<Box>{option.name}</Box>
							<Inline gap={"1"} alignItems={"center"} flexWrap={"wrap"}>
								{option.address ? (
									<Text as="span" fontSize="sm" color="muted">
										{option.address.location.name}
									</Text>
								) : null}
								<TagBadges tags={option.tags} />
								{option.deleted_at ? (
									<Badge status="warning">Disabled</Badge>
								) : null}
							</Inline>
						</Box>
					)
				}
			/>
			<AddTripSourceDialog
				open={!!newName}
				initialValues={{
					name: newName,
				}}
				minimalAddress
				onClose={() => {
					setNewName("")
				}}
				onSuccess={(tripSource) => {
					onCreateSuccess?.(tripSource)
					setNewName("")
				}}
			/>
		</>
	)
}
